const _svEN = typeof window!=='undefined' && window.location.hostname==='cad.retaliq.net'
/**
 * SettingsView — Configuración del sistema
 * Tabs: Sitios | NVRs | (futuro: Usuarios, Alertas, etc.)
 */

import { useState, useEffect, useCallback } from 'react'
import { MapPin, Users, Cpu, Layers, Server, HardDrive, Bell, Palette } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useSkinStore } from '../store/skinStore'
import CamerasConfigPanel from '../components/CamerasConfigPanel'

// ─── Error helper — Pydantic devuelve e.detail como array en respuestas 422 ──
const fmtDetail = (detail, fallback = 'Error') =>
  Array.isArray(detail)
    ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
    : (detail ? String(detail) : fallback)

// ─── Shared UI helpers ────────────────────────────────────────────────────────

const INPUT_STYLE = {
  width: '100%', padding: '7px 10px',
  borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
  background: 'var(--bg-root)', color: 'var(--text-primary)', fontSize: 12,
  boxSizing: 'border-box',
}

const BTN = (extra = {}) => ({
  padding: '6px 14px', borderRadius: 'var(--radius-sm)',
  border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
  ...extra,
})

function Label({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
      color: 'var(--text-muted)', marginBottom: 4, letterSpacing: '0.04em',
    }}>
      {children}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <Label>{label}</Label>
      {children}
    </div>
  )
}

function Modal({ title, onClose, children, width = 480 }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.45)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-surface)', borderRadius: 12,
          border: '1px solid var(--border)', width, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center',
        }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)',
            }}
          >×</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {children}
        </div>
      </div>
    </div>
  )
}

// ─── SitesTab ────────────────────────────────────────────────────────────────

function SitesTab({ token }) {
  const [tree,       setTree]       = useState([])
  const [sites,      setSites]      = useState([])
  const [zones,      setZones]      = useState([])
  const [formats,    setFormats]    = useState([])
  const [loading,    setLoading]    = useState(true)
  const [modal,      setModal]      = useState(null)
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState('')
  const [search,     setSearch]     = useState('')
  const [geocoding,  setGeocoding]  = useState(false)

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [sitesR, zonesR, formatsR] = await Promise.all([
        fetch('/api/sites/?include_inactive=true', { headers }).then(r => r.json()),
        fetch('/api/sites/zones', { headers }).then(r => r.json()),
        fetch('/api/sites/formats', { headers }).then(r => r.json()),
      ])
      setSites(Array.isArray(sitesR) ? sitesR : [])
      setZones(Array.isArray(zonesR) ? zonesR : [])
      setFormats(Array.isArray(formatsR) ? formatsR : [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { load() }, [load])

  const zoneMap   = Object.fromEntries(zones.map(z => [z.id, z.name]))
  const formatMap = Object.fromEntries(formats.map(f => [f.id, f.name]))

  const openNew = () => {
    setError('')
    setModal({ _new: true, name: '', address: '', lat: '', lon: '',
      format_id: formats[0]?.id || '', zone_id: zones[0]?.id || '',
      external_ref: '', active: true })
  }

  const openEdit = (site) => {
    setError('')
    setModal({ ...site, lat: site.lat ?? '', lon: site.lon ?? '' })
  }

  const save = async () => {
    if (!modal.name?.trim()) { setError('El nombre es obligatorio'); return }
    if (!modal.format_id)    { setError((_svEN ? 'Select a format' : 'Selecciona un formato')); return }
    if (!modal.zone_id)      { setError((_svEN ? 'Select a zone' : 'Selecciona una zona')); return }

    setSaving(true); setError('')
    try {
      const body = {
        name:         modal.name.trim(),
        address:      modal.address || null,
        lat:          modal.lat !== '' ? parseFloat(modal.lat) : null,
        lon:          modal.lon !== '' ? parseFloat(modal.lon) : null,
        format_id:    parseInt(modal.format_id),
        zone_id:      parseInt(modal.zone_id),
        external_ref: modal.external_ref || null,
        active:       modal.active,
      }
      const url    = modal._new ? '/api/sites/' : `/api/sites/${modal.id}`
      const method = modal._new ? 'POST' : 'PUT'
      const r = await fetch(url, { method, headers, body: JSON.stringify(body) })
      if (!r.ok) {
        const e = await r.json().catch(() => ({}))
        throw new Error(fmtDetail(e.detail, `Error ${r.status}`))
      }
      setModal(null)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const deactivate = async (site) => {
    if (!confirm(`¿Desactivar el sitio "${site.name}"?`)) return
    await fetch(`/api/sites/${site.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    })
    await load()
  }

  const filtered = sites.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase())
    || (s.address || '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center',
        padding: '12px 20px', borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar sitio…"
          style={{ ...INPUT_STYLE, width: 220 }}
        />
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>
          {filtered.length} sitio{filtered.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={openNew}
          style={BTN({ marginLeft: 'auto', background: 'var(--accent)', color: '#fff' })}
        >
          + Nuevo sitio
        </button>
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 12 }}>
            Cargando…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 12 }}>
            No hay sitios configurados
          </div>
        )}

        {/* Header tabla */}
        {!loading && filtered.length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 160px 120px 80px 90px 80px',
            padding: '8px 20px', background: 'var(--bg-muted)',
            borderBottom: '1px solid var(--border)',
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            color: 'var(--text-muted)', letterSpacing: '0.04em',
            position: 'sticky', top: 0, zIndex: 1,
          }}>
            <span>{_svEN ? 'Name / Address' : 'Nombre / Dirección'}</span>
            <span>Zona</span>
            <span>Formato</span>
            <span>Coords</span>
            <span>{_svEN ? 'Status' : 'Estado'}</span>
            <span></span>
          </div>
        )}

        {filtered.map(site => (
          <div
            key={site.id}
            style={{
              display: 'grid', gridTemplateColumns: '1fr 160px 120px 80px 90px 80px',
              padding: '10px 20px', borderBottom: '1px solid var(--border)',
              alignItems: 'center', fontSize: 12,
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
            onMouseLeave={e => e.currentTarget.style.background = ''}
          >
            <div>
              <div style={{ fontWeight: 600, color: site.active ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                {site.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                {site.address || '—'}
              </div>
            </div>
            <span style={{ color: 'var(--text-secondary)' }}>{zoneMap[site.zone_id] || '—'}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{formatMap[site.format_id] || '—'}</span>
            <span style={{ color: site.lat ? 'var(--success-text)' : 'var(--text-muted)' }}>
              {site.lat ? `✓ ${Number(site.lat).toFixed(3)}` : '—'}
            </span>
            <span>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                background: site.active ? 'var(--success-light)' : 'var(--bg-muted)',
                color: site.active ? 'var(--success-text)' : 'var(--text-muted)',
              }}>
                {site.active ? (_svEN ? 'Active' : 'Activo') : (_svEN ? 'Inactive' : 'Inactivo')}
              </span>
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => openEdit(site)}
                style={BTN({ background: 'var(--bg-muted)', color: 'var(--text-primary)', border: '1px solid var(--border)' })}
              >{_svEN ? 'Edit' : 'Editar'}</button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal nuevo / editar */}
      {modal && (
        <Modal
          title={modal._new ? 'Nuevo sitio' : `Editar: ${modal.name}`}
          onClose={() => setModal(null)}
          width={520}
        >
          <Field label="Nombre *">
            <input
              value={modal.name}
              onChange={e => setModal(m => ({ ...m, name: e.target.value }))}
              placeholder="Ej: Copec Vitacura"
              style={INPUT_STYLE}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Formato *">
              <select
                value={modal.format_id}
                onChange={e => setModal(m => ({ ...m, format_id: e.target.value }))}
                style={INPUT_STYLE}
              >
                <option value="">{_svEN ? 'Select…' : 'Seleccionar…'}</option>
                {formats.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </Field>
            <Field label="Zona *">
              <select
                value={modal.zone_id}
                onChange={e => setModal(m => ({ ...m, zone_id: e.target.value }))}
                style={INPUT_STYLE}
              >
                <option value="">{_svEN ? 'Select…' : 'Seleccionar…'}</option>
                {zones
                  .filter(z => !modal.format_id || z.format_id === parseInt(modal.format_id))
                  .map(z => <option key={z.id} value={z.id}>{z.name}</option>)
                }
              </select>
            </Field>
          </div>

          <Field label="Dirección">
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={modal.address || ''}
                onChange={e => setModal(m => ({ ...m, address: e.target.value }))}
                placeholder="Av. Vitacura 1234, Santiago"
                style={{ ...INPUT_STYLE, flex: 1 }}
              />
              <button
                onClick={async () => {
                  if (!modal.address?.trim()) return
                  setGeocoding(true)
                  try {
                    const r = await fetch(`/api/sites/geocode?address=${encodeURIComponent(modal.address)}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    })
                    if (r.ok) {
                      const d = await r.json()
                      setModal(m => ({ ...m, lat: d.lat, lon: d.lon }))
                    } else {
                      setError('No se encontraron coordenadas para esa dirección')
                    }
                  } catch { setError('Error al geocodificar') }
                  finally { setGeocoding(false) }
                }}
                disabled={geocoding || !modal.address?.trim()}
                title="Buscar coordenadas por dirección"
                style={BTN({ background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-primary)', flexShrink: 0, opacity: geocoding ? 0.6 : 1 })}
              >
                {geocoding ? '…' : '📍'}
              </button>
            </div>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Latitud">
              <input
                type="number" step="0.000001"
                value={modal.lat}
                onChange={e => setModal(m => ({ ...m, lat: e.target.value }))}
                placeholder="-33.4489"
                style={INPUT_STYLE}
              />
            </Field>
            <Field label="Longitud">
              <input
                type="number" step="0.000001"
                value={modal.lon}
                onChange={e => setModal(m => ({ ...m, lon: e.target.value }))}
                placeholder="-70.6693"
                style={INPUT_STYLE}
              />
            </Field>
          </div>

          <Field label="Referencia externa (VMS ID)">
            <input
              value={modal.external_ref || ''}
              onChange={e => setModal(m => ({ ...m, external_ref: e.target.value }))}
              placeholder="ACC-site-001"
              style={INPUT_STYLE}
            />
          </Field>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <input
              type="checkbox"
              id="site-active"
              checked={modal.active}
              onChange={e => setModal(m => ({ ...m, active: e.target.checked }))}
            />
            <label htmlFor="site-active" style={{ fontSize: 12, cursor: 'pointer' }}>
              Sitio activo
            </label>
          </div>

          {error && (
            <div style={{
              padding: '8px 12px', borderRadius: 6, marginBottom: 12,
              background: '#fef2f2', border: '1px solid #fecaca',
              color: '#dc2626', fontSize: 11,
            }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {!modal._new && (
              <button
                onClick={() => { setModal(null); deactivate(modal) }}
                style={BTN({ background: 'transparent', color: '#dc2626', border: '1px solid #fecaca' })}
              >
                Desactivar
              </button>
            )}
            <button
              onClick={() => setModal(null)}
              style={BTN({ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)' })}
            >
              {_svEN ? 'Cancel' : 'Cancelar'}
            </button>
            <button
              onClick={save}
              disabled={saving}
              style={BTN({ background: 'var(--accent)', color: '#fff', opacity: saving ? 0.7 : 1 })}
            >
              {saving ? (_svEN ? 'Saving…' : 'Guardando…') : (modal._new ? (_svEN ? 'Create site' : 'Crear sitio') : (_svEN ? 'Save changes' : 'Guardar cambios'))}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ─── NVRsTab ─────────────────────────────────────────────────────────────────

function NVRsTab({ token }) {
  const [nvrs,    setNvrs]    = useState([])
  const [sites,   setSites]   = useState([])
  const [loading, setLoading] = useState(true)
  const [modal,   setModal]   = useState(null)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')
  const [testResult, setTestResult] = useState(null)
  const [testing, setTesting] = useState(false)
  // Panel de configuración por canal (snapshot + checkboxes analíticas)
  const [camPanelNvr, setCamPanelNvr] = useState(null)

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [nvrsR, sitesR] = await Promise.all([
        fetch('/api/nvr/configs', { headers }).then(r => r.json()),
        fetch('/api/sites/', { headers }).then(r => r.json()),
      ])
      setNvrs(Array.isArray(nvrsR) ? nvrsR : [])
      setSites(Array.isArray(sitesR) ? sitesR : [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { load() }, [load])

  const siteMap = Object.fromEntries(sites.map(s => [s.id, s.name]))

  const openNew = () => {
    setError(''); setTestResult(null)
    setModal({
      _new: true, name: 'NVR Principal', brand: 'dahua',
      host: '', port: 80, rtsp_port: 554,
      username: 'admin', password: '', channels: 8,
      site_id: sites[0]?.id || '', enabled: true,
    })
  }

  const openEdit = (nvr) => {
    setError(''); setTestResult(null)
    setModal({ ...nvr })
  }

  const save = async () => {
    if (!modal.host?.trim()) { setError('La IP/host es obligatoria'); return }
    setSaving(true); setError('')
    try {
      const body = {
        name:      modal.name,
        brand:     modal.brand,
        host:      modal.host.trim(),
        port:      parseInt(modal.port) || 80,
        rtsp_port: parseInt(modal.rtsp_port) || 554,
        username:  modal.username,
        password:  modal.password,
        channels:  parseInt(modal.channels) || 8,
        site_id:   modal.site_id ? parseInt(modal.site_id) : null,
        enabled:   modal.enabled,
      }
      const url    = modal._new ? '/api/nvr/configs' : `/api/nvr/configs/${modal.id}`
      const method = modal._new ? 'POST' : 'PUT'
      const r = await fetch(url, { method, headers, body: JSON.stringify(body) })
      if (!r.ok) {
        const e = await r.json().catch(() => ({}))
        throw new Error(fmtDetail(e.detail, `Error ${r.status}`))
      }
      setModal(null)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const remove = async (nvr) => {
    if (!confirm(`¿Eliminar el NVR "${nvr.name}"?`)) return
    await fetch(`/api/nvr/configs/${nvr.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    })
    await load()
  }

  const testConnection = async () => {
    if (!modal.host?.trim()) return
    setTesting(true); setTestResult(null)
    try {
      const r = await fetch(`/api/nvr/${modal.id || 0}/channels`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (r.ok) {
        const ch = await r.json()
        setTestResult({ ok: true, msg: `${ch.length} canales detectados` })
      } else {
        setTestResult({ ok: false, msg: `Error ${r.status}` })
      }
    } catch (e) {
      setTestResult({ ok: false, msg: 'Sin conexión al NVR' })
    } finally {
      setTesting(false)
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center',
        padding: '12px 20px', borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {nvrs.length} NVR{nvrs.length !== 1 ? 's' : ''} configurado{nvrs.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={openNew}
          style={BTN({ marginLeft: 'auto', background: 'var(--accent)', color: '#fff' })}
        >
          + Nuevo NVR
        </button>
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 12 }}>
            Cargando…
          </div>
        )}
        {!loading && nvrs.length === 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100%', gap: 12,
            color: 'var(--text-muted)',
          }}>
            <div style={{ fontSize: 40 }}>📹</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Sin NVRs configurados</div>
            <div style={{ fontSize: 12 }}>Agrega un NVR Dahua o Hikvision para empezar.</div>
            <button
              onClick={openNew}
              style={BTN({ background: 'var(--accent)', color: '#fff', marginTop: 8 })}
            >
              + Nuevo NVR
            </button>
          </div>
        )}

        {nvrs.length > 0 && (
          <>
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 160px 80px 80px 80px',
              padding: '8px 20px', background: 'var(--bg-muted)',
              borderBottom: '1px solid var(--border)',
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              color: 'var(--text-muted)', letterSpacing: '0.04em',
              position: 'sticky', top: 0, zIndex: 1,
            }}>
              <span>{_svEN ? 'Name / Host' : 'Nombre / Host'}</span>
              <span>Marca</span>
              <span>Sitio</span>
              <span>Canales</span>
              <span>{_svEN ? 'Status' : 'Estado'}</span>
              <span></span>
            </div>

            {nvrs.map(nvr => (
              <div
                key={nvr.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 100px 160px 80px 80px 200px',
                  padding: '10px 20px', borderBottom: '1px solid var(--border)',
                  alignItems: 'center', fontSize: 12,
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{nvr.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {nvr.host}:{nvr.port}
                  </div>
                </div>
                <span style={{ textTransform: 'capitalize', color: 'var(--text-secondary)' }}>
                  {nvr.brand}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {nvr.site_id ? (siteMap[nvr.site_id] || `Sitio ${nvr.site_id}`) : '—'}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>{nvr.channels} ch</span>
                <span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                    background: nvr.enabled ? 'var(--success-light)' : 'var(--bg-muted)',
                    color: nvr.enabled ? 'var(--success-text)' : 'var(--text-muted)',
                  }}>
                    {nvr.enabled ? (_svEN ? 'Active' : 'Activo') : (_svEN ? 'Inactive' : 'Inactivo')}
                  </span>
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => setCamPanelNvr(nvr)}
                    title="Ver canales con miniatura + configurar analíticas por canal"
                    style={BTN({ background: 'var(--accent)', color: '#fff', border: 'none' })}
                  >📹 Canales</button>
                  <button
                    onClick={() => openEdit(nvr)}
                    style={BTN({ background: 'var(--bg-muted)', color: 'var(--text-primary)', border: '1px solid var(--border)' })}
                  >{_svEN ? 'Edit' : 'Editar'}</button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Panel de configuración por canal (snapshot + checkboxes 6 analíticas) */}
      {camPanelNvr && (
        <CamerasConfigPanel
          nvr={camPanelNvr}
          token={token}
          onClose={() => setCamPanelNvr(null)}
          onSaved={() => { /* opcional: refrescar lista */ }}
        />
      )}

      {/* Modal */}
      {modal && (
        <Modal
          title={modal._new ? 'Nuevo NVR' : `Editar: ${modal.name}`}
          onClose={() => setModal(null)}
          width={500}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <Field label="Nombre">
                <input
                  value={modal.name}
                  onChange={e => setModal(m => ({ ...m, name: e.target.value }))}
                  placeholder="NVR Principal"
                  style={INPUT_STYLE}
                />
              </Field>
            </div>

            <Field label="Marca">
              <select
                value={modal.brand}
                onChange={e => setModal(m => ({ ...m, brand: e.target.value }))}
                style={INPUT_STYLE}
              >
                <option value="dahua">Dahua</option>
                <option value="hikvision">Hikvision</option>
              </select>
            </Field>

            <Field label="Sitio">
              <select
                value={modal.site_id || ''}
                onChange={e => setModal(m => ({ ...m, site_id: e.target.value || null }))}
                style={INPUT_STYLE}
              >
                <option value="">Sin asignar</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>

            <Field label="IP / Host *">
              <input
                value={modal.host}
                onChange={e => setModal(m => ({ ...m, host: e.target.value }))}
                placeholder="192.168.1.100"
                style={INPUT_STYLE}
              />
            </Field>

            <Field label="Puerto HTTP">
              <input
                type="number"
                value={modal.port}
                onChange={e => setModal(m => ({ ...m, port: e.target.value }))}
                style={INPUT_STYLE}
              />
            </Field>

            <Field label="Puerto RTSP">
              <input
                type="number"
                value={modal.rtsp_port}
                onChange={e => setModal(m => ({ ...m, rtsp_port: e.target.value }))}
                style={INPUT_STYLE}
              />
            </Field>

            <Field label="Número de canales">
              <input
                type="number"
                value={modal.channels}
                onChange={e => setModal(m => ({ ...m, channels: e.target.value }))}
                style={INPUT_STYLE}
              />
            </Field>

            <Field label="Usuario">
              <input
                value={modal.username}
                onChange={e => setModal(m => ({ ...m, username: e.target.value }))}
                style={INPUT_STYLE}
              />
            </Field>

            <Field label="Contraseña">
              <input
                type="password"
                value={modal.password}
                onChange={e => setModal(m => ({ ...m, password: e.target.value }))}
                placeholder={modal._new ? '' : '(sin cambios)'}
                style={INPUT_STYLE}
              />
            </Field>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 16px' }}>
            <input
              type="checkbox"
              id="nvr-enabled"
              checked={modal.enabled}
              onChange={e => setModal(m => ({ ...m, enabled: e.target.checked }))}
            />
            <label htmlFor="nvr-enabled" style={{ fontSize: 12, cursor: 'pointer' }}>
              NVR habilitado
            </label>
          </div>

          {/* Test de conexión */}
          {!modal._new && (
            <div style={{
              padding: '10px 12px', borderRadius: 8, marginBottom: 12,
              background: 'var(--bg-muted)', border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <button
                onClick={testConnection}
                disabled={testing}
                style={BTN({ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', opacity: testing ? 0.7 : 1 })}
              >
                {testing ? '⏳ Probando…' : '🔌 Probar conexión'}
              </button>
              {testResult && (
                <span style={{
                  fontSize: 11,
                  color: testResult.ok ? 'var(--success-text)' : '#dc2626',
                }}>
                  {testResult.ok ? '✓' : '✗'} {testResult.msg}
                </span>
              )}
            </div>
          )}

          {error && (
            <div style={{
              padding: '8px 12px', borderRadius: 6, marginBottom: 12,
              background: '#fef2f2', border: '1px solid #fecaca',
              color: '#dc2626', fontSize: 11,
            }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {!modal._new && (
              <button
                onClick={() => { setModal(null); remove(modal) }}
                style={BTN({ background: 'transparent', color: '#dc2626', border: '1px solid #fecaca' })}
              >
                Eliminar
              </button>
            )}
            <button
              onClick={() => setModal(null)}
              style={BTN({ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)' })}
            >
              {_svEN ? 'Cancel' : 'Cancelar'}
            </button>
            <button
              onClick={save}
              disabled={saving}
              style={BTN({ background: 'var(--accent)', color: '#fff', opacity: saving ? 0.7 : 1 })}
            >
              {saving ? 'Guardando…' : (modal._new ? 'Crear NVR' : 'Guardar')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ─── NotificationsTab ─────────────────────────────────────────────────────────

const SEV_OPTIONS   = ['low','medium','high','critical']
const CHAN_OPTIONS  = ['whatsapp','sms']
const LEVEL_LABELS  = { 1: 'Nivel 1 — Inmediato', 2: 'Nivel 2 — Escalación', 3: 'Nivel 3 — Dirección' }

function NotificationsTab({ token }) {
  const [rules,   setRules]   = useState([])
  const [logs,    setLogs]    = useState([])
  const [status,  setStatus]  = useState(null)
  const [loading, setLoading] = useState(true)
  const [modal,   setModal]   = useState(null)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')
  const [tab,     setTab]     = useState('rules')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [testPhone, setTestPhone] = useState('')
  const [testChan,  setTestChan]  = useState('whatsapp')

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [rulesR, logsR, statusR] = await Promise.all([
        fetch('/api/notifications/rules',  { headers }).then(r => r.ok ? r.json() : []),
        fetch('/api/notifications/logs?limit=50', { headers }).then(r => r.ok ? r.json() : []),
        fetch('/api/notifications/status', { headers }).then(r => r.ok ? r.json() : null),
      ])
      setRules(Array.isArray(rulesR) ? rulesR : [])
      setLogs(Array.isArray(logsR)   ? logsR  : [])
      setStatus(statusR)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { load() }, [load])

  const openNew = () => {
    setError('')
    setModal({ _new: true, name: '', channel: 'whatsapp', phone: '', severity_threshold: 'high',
      delay_minutes: 0, level: 1, site_id: null, active: true })
  }
  const openEdit = (r) => { setError(''); setModal({ ...r }) }

  const save = async () => {
    if (!modal.name?.trim()) { setError('El nombre es obligatorio'); return }
    if (!modal.phone?.trim()) { setError('El teléfono es obligatorio'); return }
    setSaving(true); setError('')
    try {
      const url    = modal._new ? '/api/notifications/rules' : `/api/notifications/rules/${modal.id}`
      const method = modal._new ? 'POST' : 'PUT'
      const { _new, id, ...body } = modal
      const r = await fetch(url, { method, headers, body: JSON.stringify(body) })
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(fmtDetail(e.detail, `Error ${r.status}`)) }
      setModal(null); await load()
    } catch(e) { setError(e.message) } finally { setSaving(false) }
  }

  const remove = async (rule) => {
    if (!confirm(`¿Eliminar la regla "${rule.name}"?`)) return
    await fetch(`/api/notifications/rules/${rule.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
    await load()
  }

  const sendTest = async () => {
    if (!testPhone.trim()) return
    setTesting(true); setTestResult(null)
    const r = await fetch('/api/notifications/test', {
      method: 'POST', headers,
      body: JSON.stringify({ channel: testChan, phone: testPhone.trim() }),
    })
    const d = await r.json().catch(() => ({}))
    setTestResult(d)
    setTesting(false)
  }

  const SEV_COLOR = { low:'#059669', medium:'#D97706', high:'#DC2626', critical:'#7C3AED' }
  const STATUS_COLOR = { sent:'#059669', failed:'#DC2626', pending:'#D97706', skipped:'#94a3b8' }

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      {/* Tabs internas */}
      <div style={{ display:'flex', gap:4, padding:'12px 20px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
        {[['rules','Reglas de escalación'],['logs','Historial de envíos'],['test','Prueba']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding:'5px 12px', borderRadius:'var(--radius-sm)', border:'none',
            cursor:'pointer', fontSize:12,
            background: tab===id ? 'var(--bg-muted)' : 'transparent',
            color: tab===id ? 'var(--text-primary)' : 'var(--text-muted)',
            fontWeight: tab===id ? 600 : 400,
          }}>{label}</button>
        ))}
        {status && (
          <span style={{
            marginLeft:'auto', fontSize:11, padding:'4px 10px', borderRadius:20,
            background: status.configured ? 'var(--success-light)' : 'var(--warning-light)',
            color: status.configured ? 'var(--success-text)' : 'var(--warning-text)',
          }}>
            {status.configured ? '✓ Twilio configurado' : '⚠ Modo DRY-RUN (sin credenciales)'}
          </span>
        )}
      </div>

      {/* ── Reglas ── */}
      {tab === 'rules' && (
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <div style={{ padding:'10px 20px', borderBottom:'1px solid var(--border)', flexShrink:0, display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>
              {rules.length} regla{rules.length!==1?'s':''} configurada{rules.length!==1?'s':''}
            </span>
            <button onClick={openNew} style={BTN({ marginLeft:'auto', background:'var(--accent)', color:'#fff' })}>
              + Nueva regla
            </button>
          </div>

          <div style={{ flex:1, overflow:'auto' }}>
            {loading && <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)', fontSize:12 }}>Cargando…</div>}
            {!loading && rules.length === 0 && (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:12, color:'var(--text-muted)' }}>
                <div style={{ fontSize:40 }}>🔔</div>
                <div style={{ fontSize:14, fontWeight:600 }}>Sin reglas de escalación</div>
                <div style={{ fontSize:12, textAlign:'center', maxWidth:320, lineHeight:1.6 }}>
                  Define quién recibe notificaciones cuando se crea un incidente crítico,<br/>
                  o si un incidente no es atendido en cierto tiempo.
                </div>
                <button onClick={openNew} style={BTN({ background:'var(--accent)', color:'#fff', marginTop:8 })}>
                  + Nueva regla
                </button>
              </div>
            )}

            {/* Agrupar por nivel */}
            {[1,2,3].map(lvl => {
              const lvlRules = rules.filter(r => r.level === lvl)
              if (!lvlRules.length) return null
              return (
                <div key={lvl}>
                  <div style={{ padding:'8px 20px', background:'var(--bg-muted)', fontSize:10, fontWeight:700, textTransform:'uppercase', color:'var(--text-muted)', letterSpacing:'0.05em', borderBottom:'1px solid var(--border)' }}>
                    {LEVEL_LABELS[lvl]}
                  </div>
                  {lvlRules.map(rule => (
                    <div key={rule.id} style={{ display:'grid', gridTemplateColumns:'1fr 100px 80px 100px 80px 80px', padding:'10px 20px', borderBottom:'1px solid var(--border)', alignItems:'center', fontSize:12 }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bg-muted)'}
                      onMouseLeave={e=>e.currentTarget.style.background=''}
                    >
                      <div>
                        <div style={{ fontWeight:600 }}>{rule.name}</div>
                        <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'monospace' }}>{rule.phone}</div>
                      </div>
                      <span style={{ textTransform:'capitalize', color:'var(--text-secondary)' }}>{rule.channel}</span>
                      <span style={{ fontWeight:700, color: SEV_COLOR[rule.severity_threshold] }}>
                        {rule.severity_threshold}+
                      </span>
                      <span style={{ color:'var(--text-muted)', fontSize:11 }}>
                        {rule.delay_minutes === 0 ? '⚡ Inmediato' : `⏱ ${rule.delay_minutes} min`}
                      </span>
                      <span>
                        <span style={{ fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:10, background: rule.active ? 'var(--success-light)' : 'var(--bg-muted)', color: rule.active ? 'var(--success-text)' : 'var(--text-muted)' }}>
                          {rule.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </span>
                      <div style={{ display:'flex', gap:4 }}>
                        <button onClick={() => openEdit(rule)} style={BTN({ background:'var(--bg-muted)', color:'var(--text-primary)', border:'1px solid var(--border)' })}>{_svEN ? 'Edit' : 'Editar'}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Historial ── */}
      {tab === 'logs' && (
        <div style={{ flex:1, overflow:'auto' }}>
          {logs.length === 0 && !loading && (
            <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)', fontSize:12 }}>
              Sin notificaciones enviadas aún
            </div>
          )}
          {logs.length > 0 && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'60px 80px 120px 1fr 80px 120px', padding:'8px 20px', background:'var(--bg-muted)', borderBottom:'1px solid var(--border)', fontSize:10, fontWeight:700, textTransform:'uppercase', color:'var(--text-muted)', letterSpacing:'0.04em', position:'sticky', top:0 }}>
                <span>Inc.</span><span>{_svEN ? 'Channel' : 'Canal'}</span><span>{_svEN ? 'Phone' : 'Teléfono'}</span><span>{_svEN ? 'Message' : 'Mensaje'}</span><span>{_svEN ? 'Status' : 'Estado'}</span><span>{_svEN ? 'Sent' : 'Enviado'}</span>
              </div>
              {logs.map(log => (
                <div key={log.id} style={{ display:'grid', gridTemplateColumns:'60px 80px 120px 1fr 80px 120px', padding:'8px 20px', borderBottom:'1px solid var(--border)', fontSize:11, alignItems:'start' }}>
                  <span style={{ color:'var(--accent)', fontWeight:600 }}>#{log.incident_id}</span>
                  <span style={{ textTransform:'capitalize', color:'var(--text-secondary)' }}>{log.channel}</span>
                  <span style={{ fontFamily:'monospace', fontSize:10 }}>{log.phone}</span>
                  <span style={{ color:'var(--text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{log.message?.slice(0,80)}</span>
                  <span style={{ fontWeight:700, fontSize:10, color: STATUS_COLOR[log.status] || 'var(--text-muted)' }}>{log.status}</span>
                  <span style={{ color:'var(--text-muted)', fontSize:10 }}>{new Date(log.sent_at).toLocaleString('es-CL',{dateStyle:'short',timeStyle:'short'})}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ── Prueba ── */}
      {tab === 'test' && (
        <div style={{ flex:1, overflow:'auto', padding:24 }}>
          <div style={{ maxWidth:420 }}>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>🔔 Enviar mensaje de prueba</div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:20, lineHeight:1.6 }}>
              Verifica que Twilio esté correctamente configurado enviando un mensaje de prueba.
              {status && !status.configured && (
                <span style={{ display:'block', marginTop:8, padding:'8px 12px', borderRadius:6, background:'var(--warning-light)', color:'var(--warning-text)', fontSize:11 }}>
                  ⚠ Twilio no está configurado — el mensaje se simulará (DRY-RUN).
                  Para activar: agrega TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN y TWILIO_FROM_NUMBER al .env del servidor.
                </span>
              )}
            </div>

            <Field label="Canal">
              <select value={testChan} onChange={e=>setTestChan(e.target.value)} style={INPUT_STYLE}>
                <option value="whatsapp">WhatsApp</option>
                <option value="sms">SMS</option>
              </select>
            </Field>
            <Field label="Número de destino">
              <input value={testPhone} onChange={e=>setTestPhone(e.target.value)} placeholder="+56912345678" style={INPUT_STYLE} />
            </Field>

            <button onClick={sendTest} disabled={testing || !testPhone.trim()}
              style={BTN({ background:'var(--accent)', color:'#fff', opacity: testing||!testPhone.trim() ? 0.6 : 1, marginTop:8 })}>
              {testing ? '⏳ Enviando…' : '🚀 Enviar prueba'}
            </button>

            {testResult && (
              <div style={{ marginTop:16, padding:'12px 16px', borderRadius:8, border:'1px solid var(--border)', background: testResult.ok ? 'var(--success-light)' : '#fef2f2' }}>
                <div style={{ fontWeight:700, fontSize:12, color: testResult.ok ? 'var(--success-text)' : '#dc2626' }}>
                  {testResult.ok ? '✓ Enviado correctamente' : '✗ Error al enviar'}
                  {testResult.dry_run && ' (DRY-RUN)'}
                </div>
                {testResult.sid && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>SID: {testResult.sid}</div>}
                {testResult.error && <div style={{ fontSize:11, color:'#dc2626', marginTop:4 }}>{testResult.error}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal regla ── */}
      {modal && (
        <Modal title={modal._new ? 'Nueva regla de escalación' : `Editar: ${modal.name}`} onClose={() => setModal(null)} width={500}>
          <Field label="Nombre de la regla *">
            <input value={modal.name} onChange={e=>setModal(m=>({...m,name:e.target.value}))} placeholder="Supervisor turno noche" style={INPUT_STYLE} />
          </Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Canal">
              <select value={modal.channel} onChange={e=>setModal(m=>({...m,channel:e.target.value}))} style={INPUT_STYLE}>
                {CHAN_OPTIONS.map(c => <option key={c} value={c}>{c === 'whatsapp' ? 'WhatsApp' : 'SMS'}</option>)}
              </select>
            </Field>
            <Field label="Nivel de escalación">
              <select value={modal.level} onChange={e=>setModal(m=>({...m,level:parseInt(e.target.value)}))} style={INPUT_STYLE}>
                <option value={1}>Nivel 1 — Inmediato</option>
                <option value={2}>Nivel 2 — Escalación</option>
                <option value={3}>Nivel 3 — Dirección</option>
              </select>
            </Field>
          </div>
          <Field label="Número de teléfono *">
            <input value={modal.phone} onChange={e=>setModal(m=>({...m,phone:e.target.value}))} placeholder="+56912345678" style={INPUT_STYLE} />
          </Field>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Severidad mínima">
              <select value={modal.severity_threshold} onChange={e=>setModal(m=>({...m,severity_threshold:e.target.value}))} style={INPUT_STYLE}>
                {SEV_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </Field>
            <Field label="Demora (minutos)">
              <input type="number" min={0} max={1440} value={modal.delay_minutes} onChange={e=>setModal(m=>({...m,delay_minutes:parseInt(e.target.value)||0}))} style={INPUT_STYLE} />
              <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:3 }}>
                0 = notificar al crear el incidente
              </div>
            </Field>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16, marginTop:4 }}>
            <input type="checkbox" id="rule-active" checked={modal.active} onChange={e=>setModal(m=>({...m,active:e.target.checked}))} />
            <label htmlFor="rule-active" style={{ fontSize:12, cursor:'pointer' }}>Regla activa</label>
          </div>
          {error && <div style={{ padding:'8px 12px', borderRadius:6, marginBottom:12, background:'#fef2f2', border:'1px solid #fecaca', color:'#dc2626', fontSize:11 }}>⚠️ {error}</div>}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            {!modal._new && (
              <button onClick={() => { setModal(null); remove(modal) }} style={BTN({ background:'transparent', color:'#dc2626', border:'1px solid #fecaca' })}>Eliminar</button>
            )}
            <button onClick={() => setModal(null)} style={BTN({ background:'transparent', color:'var(--text-muted)', border:'1px solid var(--border)' })}>{_svEN ? 'Cancel' : 'Cancelar'}</button>
            <button onClick={save} disabled={saving} style={BTN({ background:'var(--accent)', color:'#fff', opacity:saving?0.7:1 })}>
              {saving ? 'Guardando…' : (modal._new ? 'Crear regla' : 'Guardar')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ─── SettingsView ─────────────────────────────────────────────────────────────

// ─── UsersTab ─────────────────────────────────────────────────────────────────

const ROLES_LABELS = _svEN ? { superadmin: 'Super Admin', admin: 'Admin', supervisor: 'Supervisor', operator: 'Operator', guard: 'Guard', analyst: 'Analyst' } : { superadmin: 'Super Admin', admin: 'Admin', supervisor: 'Supervisor', operator: 'Operador', guard: 'Guardia', analyst: 'Analista' }
const ROLES_LIST   = ['admin', 'supervisor', 'operator', 'guard', 'analyst']
const ROLE_COLOR   = { superadmin: '#7c3aed', admin: '#2563eb', supervisor: '#0891b2', operator: '#059669', guard: '#d97706', analyst: '#6b7280' }

function UsersTab({ token }) {
  const me = useAuthStore(s => s.user)
  const [users,   setUsers]   = useState([])
  const [formats, setFormats] = useState([])
  const [zones,   setZones]   = useState([])
  const [loading, setLoading] = useState(true)
  const [modal,   setModal]   = useState(null)   // null | 'new' | user | 'me-password'
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')
  const [showInactive, setShowInactive] = useState(false)

  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [ur, fr, zr] = await Promise.all([
        fetch(`/api/users/?active_only=${!showInactive}`, { headers: h }).then(r => r.json()),
        fetch('/api/sites/formats', { headers: h }).then(r => r.json()),
        fetch('/api/sites/zones',   { headers: h }).then(r => r.json()),
      ])
      setUsers(Array.isArray(ur) ? ur : [])
      setFormats(Array.isArray(fr) ? fr : [])
      setZones(Array.isArray(zr) ? zr : [])
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [token, showInactive])

  useEffect(() => { load() }, [load])

  const openNew = () => {
    setError('')
    setModal({ _new: true, nombre: '', email: '', password: '', rol: 'operator', format_id: '', zone_id: '', active: true })
  }

  const openEdit = u => { setError(''); setModal({ ...u, password: '' }) }

  const save = async () => {
    if (!modal.nombre?.trim()) { setError('El nombre es obligatorio'); return }
    if (!modal.email?.trim())  { setError('El email es obligatorio');  return }
    if (modal._new && !modal.password) { setError('La contraseña es obligatoria'); return }
    setSaving(true); setError('')
    try {
      const body = {
        nombre: modal.nombre.trim(), email: modal.email.trim(), rol: modal.rol,
        format_id: modal.format_id ? parseInt(modal.format_id) : null,
        zone_id:   modal.zone_id   ? parseInt(modal.zone_id)   : null,
        active: modal.active,
        ...(modal._new ? { password: modal.password } : {}),
      }
      const url    = modal._new ? '/api/users/' : `/api/users/${modal.id}`
      const method = modal._new ? 'POST' : 'PUT'
      const r = await fetch(url, { method, headers: h, body: JSON.stringify(body) })
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(fmtDetail(e.detail, `Error ${r.status}`)) }
      setModal(null); await load()
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  const resetPwd = async (userId, newPwd) => {
    setSaving(true); setError('')
    try {
      const r = await fetch(`/api/users/${userId}/reset-password`, {
        method: 'POST', headers: h, body: JSON.stringify({ new_password: newPwd })
      })
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(fmtDetail(e.detail, `Error ${r.status}`)) }
      setModal(null)
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  const deactivate = async u => {
    if (!confirm(`¿Desactivar al usuario "${u.nombre}"?`)) return
    await fetch(`/api/users/${u.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
    await load()
  }

  const fmtMap  = Object.fromEntries(formats.map(f => [f.id, f.name]))
  const zoneMap = Object.fromEntries(zones.map(z => [z.id, z.name]))

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{users.length} usuario{users.length !== 1 ? 's' : ''}</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', marginLeft: 8 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Mostrar inactivos
        </label>
        <button onClick={() => { setError(''); setModal('me-password') }} style={BTN({ background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-primary)' })}>
          🔑 Mi contraseña
        </button>
        <button onClick={openNew} style={BTN({ marginLeft: 'auto', background: 'var(--accent)', color: '#fff' })}>+ Nuevo usuario</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 12 }}>Cargando…</div>}

        {!loading && users.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 120px 120px 80px 80px', padding: '8px 20px', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.04em', position: 'sticky', top: 0 }}>
            <span>{_svEN ? 'Name / Email' : 'Nombre / Email'}</span><span>{_svEN ? 'Role' : 'Rol'}</span><span>{_svEN ? 'Format' : 'Formato'}</span><span>{_svEN ? 'Zone' : 'Zona'}</span><span>{_svEN ? 'Status' : 'Estado'}</span><span></span>
          </div>
        )}

        {users.map(u => (
          <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '1fr 180px 120px 120px 80px 80px', padding: '10px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 12, opacity: u.active ? 1 : 0.55 }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
            onMouseLeave={e => e.currentTarget.style.background = ''}>
            <div>
              <div style={{ fontWeight: 600 }}>{u.nombre}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.email}</div>
            </div>
            <span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: (ROLE_COLOR[u.rol] || '#999') + '20', color: ROLE_COLOR[u.rol] || '#999' }}>
                {ROLES_LABELS[u.rol] || u.rol}
              </span>
            </span>
            <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{fmtMap[u.format_id] || '—'}</span>
            <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{zoneMap[u.zone_id] || '—'}</span>
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: u.active ? 'var(--success-light)' : 'var(--bg-muted)', color: u.active ? 'var(--success-text)' : 'var(--text-muted)' }}>
              {u.active ? (_svEN ? 'Active' : 'Activo') : (_svEN ? 'Inactive' : 'Inactivo')}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => openEdit(u)} style={BTN({ background: 'var(--bg-muted)', color: 'var(--text-primary)', border: '1px solid var(--border)' })}>{_svEN ? 'Edit' : 'Editar'}</button>
            </div>
          </div>
        ))}
        {!loading && users.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 12 }}>No hay usuarios</div>}
      </div>

      {/* Modal crear/editar usuario */}
      {modal && modal !== 'me-password' && (
        <Modal title={modal._new ? 'Nuevo usuario' : `Editar: ${modal.nombre}`} onClose={() => setModal(null)} width={480}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Nombre completo *">
              <input value={modal.nombre} onChange={e => setModal(m => ({ ...m, nombre: e.target.value }))} style={INPUT_STYLE} placeholder="Ana García" />
            </Field>
            <Field label="Email *">
              <input value={modal.email} onChange={e => setModal(m => ({ ...m, email: e.target.value }))} style={INPUT_STYLE} placeholder="ana@empresa.cl" disabled={!modal._new} />
            </Field>
          </div>

          {modal._new && (
            <Field label="Contraseña *">
              <input type="password" value={modal.password} onChange={e => setModal(m => ({ ...m, password: e.target.value }))} style={INPUT_STYLE} placeholder="Mín. 8 caracteres" />
            </Field>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Rol *">
              <select value={modal.rol} onChange={e => setModal(m => ({ ...m, rol: e.target.value }))} style={INPUT_STYLE}>
                {ROLES_LIST.map(r => <option key={r} value={r}>{ROLES_LABELS[r]}</option>)}
              </select>
            </Field>
            <Field label="Scope — Formato (opcional)">
              <select value={modal.format_id || ''} onChange={e => setModal(m => ({ ...m, format_id: e.target.value }))} style={INPUT_STYLE}>
                <option value="">Todos los formatos</option>
                {formats.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Scope — Zona (opcional)">
            <select value={modal.zone_id || ''} onChange={e => setModal(m => ({ ...m, zone_id: e.target.value }))} style={INPUT_STYLE}>
              <option value="">Todas las zonas</option>
              {zones.filter(z => !modal.format_id || z.format_id === parseInt(modal.format_id)).map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </Field>

          {!modal._new && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <input type="checkbox" id="u-active" checked={modal.active} onChange={e => setModal(m => ({ ...m, active: e.target.checked }))} />
                <label htmlFor="u-active" style={{ fontSize: 12, cursor: 'pointer' }}>Usuario activo</label>
              </div>
              <ResetPwdInline userId={modal.id} token={token} onDone={() => setModal(null)} />
            </>
          )}

          {error && <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 11 }}>⚠️ {error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {!modal._new && modal.id !== me?.id && (
              <button onClick={() => { setModal(null); deactivate(modal) }} style={BTN({ background: 'transparent', color: '#dc2626', border: '1px solid #fecaca' })}>Desactivar</button>
            )}
            <button onClick={() => setModal(null)} style={BTN({ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)' })}>{_svEN ? 'Cancel' : 'Cancelar'}</button>
            <button onClick={save} disabled={saving} style={BTN({ background: 'var(--accent)', color: '#fff', opacity: saving ? 0.7 : 1 })}>
              {saving ? 'Guardando…' : (modal._new ? 'Crear usuario' : 'Guardar')}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal cambio de contraseña propio */}
      {modal === 'me-password' && <ChangeOwnPasswordModal token={token} onClose={() => setModal(null)} />}
    </div>
  )
}

function ResetPwdInline({ userId, token, onDone }) {
  const [open, setOpen]   = useState(false)
  const [pwd,  setPwd]    = useState('')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg]     = useState('')

  const submit = async () => {
    if (pwd.length < 8) { setMsg('Mínimo 8 caracteres'); return }
    setSaving(true)
    try {
      const r = await fetch(`/api/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: pwd }),
      })
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(fmtDetail(e.detail, 'Error')) }
      setMsg('✓ Contraseña actualizada'); setPwd(''); setOpen(false); onDone()
    } catch (e) { setMsg(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginBottom: 12 }}>
      <button onClick={() => setOpen(o => !o)} style={BTN({ background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-primary)', marginBottom: open ? 8 : 0 })}>
        🔑 Resetear contraseña
      </button>
      {open && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Nueva contraseña" style={{ ...INPUT_STYLE, flex: 1 }} />
          <button onClick={submit} disabled={saving} style={BTN({ background: 'var(--accent)', color: '#fff' })}>{saving ? '…' : 'Guardar'}</button>
        </div>
      )}
      {msg && <div style={{ fontSize: 11, color: msg.startsWith('✓') ? '#059669' : '#dc2626', marginTop: 4 }}>{msg}</div>}
    </div>
  )
}

function ChangeOwnPasswordModal({ token, onClose }) {
  const [current, setCurrent] = useState('')
  const [next,    setNext]    = useState('')
  const [saving,  setSaving]  = useState(false)
  const [msg,     setMsg]     = useState('')

  const submit = async () => {
    if (!current || !next) { setMsg('Completa ambos campos'); return }
    if (next.length < 8)   { setMsg('Mínimo 8 caracteres');  return }
    setSaving(true); setMsg('')
    try {
      const r = await fetch('/api/auth/me/change-password', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: next }),
      })
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(fmtDetail(e.detail, 'Error')) }
      setMsg('✓ Contraseña actualizada correctamente')
      setTimeout(onClose, 1500)
    } catch (e) { setMsg(e.message) }
    finally { setSaving(false) }
  }

  return (
    <Modal title="Cambiar mi contraseña" onClose={onClose} width={380}>
      <Field label="Contraseña actual">
        <input type="password" value={current} onChange={e => setCurrent(e.target.value)} style={INPUT_STYLE} />
      </Field>
      <Field label="Nueva contraseña">
        <input type="password" value={next} onChange={e => setNext(e.target.value)} style={INPUT_STYLE} placeholder="Mín. 8 caracteres" />
      </Field>
      {msg && <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 12, background: msg.startsWith('✓') ? '#f0fdf4' : '#fef2f2', border: `1px solid ${msg.startsWith('✓') ? '#bbf7d0' : '#fecaca'}`, color: msg.startsWith('✓') ? '#166534' : '#dc2626', fontSize: 11 }}>{msg}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={BTN({ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)' })}>{_svEN ? 'Cancel' : 'Cancelar'}</button>
        <button onClick={submit} disabled={saving} style={BTN({ background: 'var(--accent)', color: '#fff', opacity: saving ? 0.7 : 1 })}>{saving ? 'Guardando…' : 'Actualizar'}</button>
      </div>
    </Modal>
  )
}


// ─── DevicesTab ───────────────────────────────────────────────────────────────

const DEVICE_TYPE_LABELS = { camera: '📷 Cámara', speaker: '🔊 Parlante', panel: '📟 Panel', nvr: '💾 NVR', sensor: '🔍 Sensor', access: '🚪 Control acceso' }

function DevicesTab({ token }) {
  const [sites,      setSites]      = useState([])
  const [selectedSite, setSelectedSite] = useState(null)
  const [devices,    setDevices]    = useState([])
  const [loading,    setLoading]    = useState(false)
  const [modal,      setModal]      = useState(null)
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState('')

  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

  useEffect(() => {
    fetch('/api/sites/?include_inactive=false', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setSites(Array.isArray(d) ? d : []); if (d.length) setSelectedSite(d[0]) })
      .catch(() => {})
  }, [token])

  const loadDevices = useCallback(async (site) => {
    if (!site) return
    setLoading(true)
    try {
      const d = await fetch(`/api/devices/by-site/${site.id}?include_inactive=true`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
      setDevices(Array.isArray(d) ? d : [])
    } catch { setDevices([]) }
    finally { setLoading(false) }
  }, [token])

  useEffect(() => { loadDevices(selectedSite) }, [selectedSite, loadDevices])

  const openNew = () => {
    setError('')
    setModal({ _new: true, name: '', type: 'camera', brand: '', model: '', ip: '', external_id: '' })
  }
  const openEdit = d => { setError(''); setModal({ ...d }) }

  const save = async () => {
    if (!modal.name?.trim()) { setError('El nombre es obligatorio'); return }
    setSaving(true); setError('')
    try {
      const body = { name: modal.name.trim(), type: modal.type, brand: modal.brand || null, model: modal.model || null, ip: modal.ip || null, external_id: modal.external_id || null, ...(modal._new ? { site_id: selectedSite.id } : { active: modal.active }) }
      const url    = modal._new ? '/api/devices/' : `/api/devices/${modal.id}`
      const method = modal._new ? 'POST' : 'PUT'
      const r = await fetch(url, { method, headers: h, body: JSON.stringify(body) })
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(fmtDetail(e.detail, `Error ${r.status}`)) }
      setModal(null); loadDevices(selectedSite)
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  const remove = async d => {
    if (!confirm(`¿Eliminar "${d.name}"?`)) return
    await fetch(`/api/devices/${d.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
    loadDevices(selectedSite)
  }

  return (
    <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      {/* Sidebar sitios */}
      <div style={{ width: 220, borderRight: '1px solid var(--border)', overflow: 'auto', flexShrink: 0 }}>
        <div style={{ padding: '10px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>{_svEN ? 'Sites' : 'Sitios'}</div>
        {sites.map(s => (
          <div key={s.id} onClick={() => setSelectedSite(s)} style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 12, fontWeight: selectedSite?.id === s.id ? 600 : 400, background: selectedSite?.id === s.id ? 'var(--accent)' : 'transparent', color: selectedSite?.id === s.id ? '#fff' : 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}>
            {s.name}
          </div>
        ))}
      </div>

      {/* Panel dispositivos */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedSite?.name || '—'}</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{devices.length} dispositivo{devices.length !== 1 ? 's' : ''}</span>
          <button onClick={openNew} disabled={!selectedSite} style={BTN({ marginLeft: 'auto', background: 'var(--accent)', color: '#fff', opacity: selectedSite ? 1 : 0.5 })}>+ Agregar dispositivo</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 12 }}>Cargando…</div>}
          {!loading && devices.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 12 }}>Sin dispositivos en este sitio</div>}

          {!loading && devices.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 120px 120px 80px 80px', padding: '8px 20px', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', position: 'sticky', top: 0 }}>
              <span>{_svEN ? 'Name' : 'Nombre'}</span><span>{_svEN ? 'Type' : 'Tipo'}</span><span>{_svEN ? 'Brand / Model' : 'Marca / Modelo'}</span><span>IP</span><span>{_svEN ? 'Status' : 'Estado'}</span><span></span>
            </div>
          )}

          {devices.map(d => (
            <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 120px 120px 80px 80px', padding: '10px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 12, opacity: d.active ? 1 : 0.5 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <span style={{ fontWeight: 500 }}>{d.name}</span>
              <span style={{ fontSize: 11 }}>{DEVICE_TYPE_LABELS[d.type] || d.type}</span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{[d.brand, d.model].filter(Boolean).join(' ') || '—'}</span>
              <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{d.ip || '—'}</span>
              <span>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: d.online ? '#d1fae5' : 'var(--bg-muted)', color: d.online ? '#065f46' : 'var(--text-muted)' }}>
                  {d.online ? 'Online' : 'Offline'}
                </span>
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => openEdit(d)} style={BTN({ background: 'var(--bg-muted)', color: 'var(--text-primary)', border: '1px solid var(--border)' })}>{_svEN ? 'Edit' : 'Editar'}</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {modal && (
        <Modal title={modal._new ? 'Nuevo dispositivo' : `Editar: ${modal.name}`} onClose={() => setModal(null)} width={460}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Nombre *">
              <input value={modal.name} onChange={e => setModal(m => ({ ...m, name: e.target.value }))} style={INPUT_STYLE} placeholder="Cámara entrada norte" />
            </Field>
            <Field label="Tipo *">
              <select value={modal.type} onChange={e => setModal(m => ({ ...m, type: e.target.value }))} style={INPUT_STYLE}>
                {Object.entries(DEVICE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Marca">
              <input value={modal.brand || ''} onChange={e => setModal(m => ({ ...m, brand: e.target.value }))} style={INPUT_STYLE} placeholder="Avigilon, Bosch…" />
            </Field>
            <Field label="Modelo">
              <input value={modal.model || ''} onChange={e => setModal(m => ({ ...m, model: e.target.value }))} style={INPUT_STYLE} placeholder="H4A-BO-IR-30W-D" />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="IP">
              <input value={modal.ip || ''} onChange={e => setModal(m => ({ ...m, ip: e.target.value }))} style={INPUT_STYLE} placeholder="192.168.1.50" />
            </Field>
            <Field label="ID externo (VMS)">
              <input value={modal.external_id || ''} onChange={e => setModal(m => ({ ...m, external_id: e.target.value }))} style={INPUT_STYLE} placeholder="acc-cam-001" />
            </Field>
          </div>
          {!modal._new && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <input type="checkbox" id="dev-active" checked={modal.active} onChange={e => setModal(m => ({ ...m, active: e.target.checked }))} />
              <label htmlFor="dev-active" style={{ fontSize: 12, cursor: 'pointer' }}>Dispositivo activo</label>
            </div>
          )}
          {error && <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 11 }}>⚠️ {error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {!modal._new && <button onClick={() => { setModal(null); remove(modal) }} style={BTN({ background: 'transparent', color: '#dc2626', border: '1px solid #fecaca' })}>Eliminar</button>}
            <button onClick={() => setModal(null)} style={BTN({ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)' })}>{_svEN ? 'Cancel' : 'Cancelar'}</button>
            <button onClick={save} disabled={saving} style={BTN({ background: 'var(--accent)', color: '#fff', opacity: saving ? 0.7 : 1 })}>{saving ? 'Guardando…' : (modal._new ? 'Agregar' : 'Guardar')}</button>
          </div>
        </Modal>
      )}
    </div>
  )
}


// ─── FormatsZonesTab ──────────────────────────────────────────────────────────

function FormatsZonesTab({ token }) {
  const [formats,  setFormats]  = useState([])
  const [zones,    setZones]    = useState([])
  const [loading,  setLoading]  = useState(true)
  const [modal,    setModal]    = useState(null)  // { _type:'format'|'zone', _new, ...fields }
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState('')

  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [fr, zr] = await Promise.all([
        fetch('/api/sites/formats', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch('/api/sites/zones',   { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ])
      setFormats(Array.isArray(fr) ? fr : [])
      setZones(Array.isArray(zr) ? zr : [])
    } finally { setLoading(false) }
  }, [token])

  useEffect(() => { load() }, [load])

  const saveModal = async () => {
    setSaving(true); setError('')
    try {
      const isFormat = modal._type === 'format'
      const body = isFormat
        ? { name: modal.name, slug: modal.name.toLowerCase().replace(/\s+/g, '-'), icon: modal.icon || '⛽' }
        : { name: modal.name, format_id: parseInt(modal.format_id) }
      const base   = isFormat ? '/api/sites/formats' : '/api/sites/zones/'
      const url    = modal._new ? base : `${base}${modal._new ? '' : modal.id}`
      const method = modal._new ? 'POST' : 'PUT'
      const r = await fetch(modal._new ? base : `${base.replace(/\/$/, '')}/${modal.id}`, { method, headers: h, body: JSON.stringify(body) })
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(fmtDetail(e.detail, `Error ${r.status}`)) }
      setModal(null); load()
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  const deleteZone = async z => {
    if (!confirm(`¿Eliminar la zona "${z.name}"?`)) return
    await fetch(`/api/sites/zones/${z.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
    load()
  }

  const fmtMap = Object.fromEntries(formats.map(f => [f.id, f.name]))

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: 24 }}>
      {loading && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Cargando…</div>}

      {/* Formatos */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{_svEN ? 'Business formats' : 'Formatos de negocio'}</span>
          <button onClick={() => { setError(''); setModal({ _type: 'format', _new: true, name: '', icon: '⛽' }) }}
            style={BTN({ marginLeft: 'auto', background: 'var(--accent)', color: '#fff' })}>+ Nuevo formato</button>
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {formats.length === 0 && <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>Sin formatos</div>}
          {formats.map((f, i) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: i < formats.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
              <span style={{ fontSize: 18, marginRight: 10 }}>{f.icon || '⛽'}</span>
              <div>
                <div style={{ fontWeight: 600 }}>{f.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>slug: {f.slug}</div>
              </div>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>{zones.filter(z => z.format_id === f.id).length} zona(s)</span>
              <button onClick={() => { setError(''); setModal({ _type: 'format', ...f }) }} style={BTN({ marginLeft: 12, background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-primary)' })}>{_svEN ? 'Edit' : 'Editar'}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Zonas */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{_svEN ? 'Geographic zones' : 'Zonas geográficas'}</span>
          <button onClick={() => { setError(''); setModal({ _type: 'zone', _new: true, name: '', format_id: formats[0]?.id || '' }) }}
            style={BTN({ marginLeft: 'auto', background: 'var(--accent)', color: '#fff' })}>+ Nueva zona</button>
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {zones.length === 0 && <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>Sin zonas</div>}
          {zones.map((z, i) => (
            <div key={z.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: i < zones.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{z.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtMap[z.format_id] || '—'}</div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button onClick={() => { setError(''); setModal({ _type: 'zone', ...z }) }} style={BTN({ background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-primary)' })}>{_svEN ? 'Edit' : 'Editar'}</button>
                <button onClick={() => deleteZone(z)} style={BTN({ background: 'transparent', color: '#dc2626', border: '1px solid #fecaca' })}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {modal && (
        <Modal title={modal._type === 'format' ? (modal._new ? 'Nuevo formato' : 'Editar formato') : (modal._new ? 'Nueva zona' : 'Editar zona')} onClose={() => setModal(null)} width={400}>
          <Field label="Nombre *">
            <input value={modal.name || ''} onChange={e => setModal(m => ({ ...m, name: e.target.value }))} style={INPUT_STYLE} placeholder={modal._type === 'format' ? 'Copec Full Service' : 'Región Metropolitana'} />
          </Field>
          {modal._type === 'format' && (
            <Field label="Ícono (emoji)">
              <input value={modal.icon || ''} onChange={e => setModal(m => ({ ...m, icon: e.target.value }))} style={INPUT_STYLE} placeholder="⛽" maxLength={4} />
            </Field>
          )}
          {modal._type === 'zone' && (
            <Field label="Formato *">
              <select value={modal.format_id || ''} onChange={e => setModal(m => ({ ...m, format_id: e.target.value }))} style={INPUT_STYLE}>
                <option value="">{_svEN ? 'Select…' : 'Seleccionar…'}</option>
                {formats.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </Field>
          )}
          {error && <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 11 }}>⚠️ {error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setModal(null)} style={BTN({ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)' })}>{_svEN ? 'Cancel' : 'Cancelar'}</button>
            <button onClick={saveModal} disabled={saving} style={BTN({ background: 'var(--accent)', color: '#fff', opacity: saving ? 0.7 : 1 })}>{saving ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </Modal>
      )}
    </div>
  )
}


// ─── EdgeNodesTab ─────────────────────────────────────────────────────────────

const DEFAULT_CFG = () => ({
  site_name: '',
  timezone: 'America/Santiago',
  nvrs: [{ id: 'nvr-01', name: 'NVR Principal', brand: 'dahua', host: '', port: 80, rtsp_port: 554, username: 'admin', password: '', channels: 8, enabled: true }],
  cameras: [],
  analytics: { model_path: '/var/retaliq/models/yolov8n.mlpackage', confidence: 0.45, lpr_confidence: 0.70, lpr_min_area: 800, person_alert_zones: [], vehicle_dwell_seconds: 90 },
  mediamtx: { binary: '/usr/local/bin/mediamtx', api_port: 9997, rtsp_port: 8554, webrtc_port: 8889, hls_port: 8888 },
})

const ANALYTICS_OPTS = ['lpr', 'vehicle', 'person']

const COMMON_ZONES = ['acceso', 'pista', 'cisterna', 'perimetro', 'interior', 'bodega', 'estacionamiento', 'general']

function EdgeNodesTab({ token }) {
  const [nodes,      setNodes]      = useState([])
  const [loading,    setLoading]    = useState(true)
  const [keyModal,   setKeyModal]   = useState(null)   // node con nueva API key
  const [addModal,   setAddModal]   = useState(false)
  const [addForm,    setAddForm]    = useState({ site_id_ext: '', site_name: '' })
  const [addLoading, setAddLoading] = useState(false)
  const [addError,   setAddError]   = useState('')
  const [cfgModal,   setCfgModal]   = useState(null)   // { node, config, tab, saving, error }
  const [cfgLoading, setCfgLoading] = useState(false)

  const h = { Authorization: `Bearer ${token}` }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const d = await fetch('/api/edge/nodes', { headers: h }).then(r => r.json())
      setNodes(Array.isArray(d) ? d : [])
    } finally { setLoading(false) }
  }, [token])

  useEffect(() => { load() }, [load])

  const deactivate = async node => {
    if (!confirm(`¿Desactivar el nodo "${node.site_name || node.site_id_ext}"?`)) return
    await fetch(`/api/edge/nodes/${node.id}`, { method: 'DELETE', headers: h })
    load()
  }

  const reactivate = async node => {
    await fetch(`/api/edge/nodes/${node.id}/reactivate`, { method: 'POST', headers: h })
    load()
  }

  const regenerateKey = async node => {
    if (!confirm(`¿Regenerar la API key de "${node.site_name || node.site_id_ext}"?\nEl nodo deberá actualizarse con la nueva clave.`)) return
    const r = await fetch(`/api/edge/nodes/${node.id}/regenerate-key`, { method: 'POST', headers: h })
    if (r.ok) {
      const d = await r.json()
      setKeyModal({ ...node, new_api_key: d.api_key })
      load()
    }
  }

  const openConfig = async (node) => {
    setCfgLoading(true)
    try {
      const r = await fetch(`/api/edge/config/${node.id}`, { headers: h })
      if (r.ok) {
        const d = await r.json()
        const base = d.config || {}
        setCfgModal({
          node,
          tab: 'general',
          config: { ...DEFAULT_CFG(), ...base, site_name: base.site_name || node.site_name || '' },
          saving: false,
          error: '',
        })
      } else {
        // Nodo sin config remota — abrir con defaults
        setCfgModal({
          node,
          tab: 'general',
          config: { ...DEFAULT_CFG(), site_name: node.site_name || '' },
          saving: false,
          error: '',
        })
      }
    } catch {
      setCfgModal({
        node,
        tab: 'general',
        config: { ...DEFAULT_CFG(), site_name: node.site_name || '' },
        saving: false,
        error: '',
      })
    } finally { setCfgLoading(false) }
  }

  const saveConfig = async () => {
    if (!cfgModal) return
    if (!cfgModal.config.nvrs[0]?.host?.trim()) {
      setCfgModal(m => ({ ...m, error: 'La IP del NVR es obligatoria', tab: 'nvrs' }))
      return
    }
    setCfgModal(m => ({ ...m, saving: true, error: '' }))
    try {
      const r = await fetch(`/api/edge/config/${cfgModal.node.id}`, {
        method: 'PUT',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify(cfgModal.config),
      })
      if (!r.ok) {
        const e = await r.json().catch(() => ({}))
        setCfgModal(m => ({ ...m, saving: false, error: fmtDetail(e.detail, 'Error al guardar') }))
        return
      }
      setCfgModal(null)
      load()
    } catch (e) {
      setCfgModal(m => ({ ...m, saving: false, error: String(e) }))
    }
  }

  const updateCfg = (path, value) => {
    setCfgModal(m => {
      const c = JSON.parse(JSON.stringify(m.config))
      const parts = path.split('.')
      let obj = c
      for (let i = 0; i < parts.length - 1; i++) {
        if (parts[i].includes('[')) {
          const [key, idx] = parts[i].split('[')
          obj = obj[key][parseInt(idx)]
        } else {
          obj = obj[parts[i]]
        }
      }
      const last = parts[parts.length - 1]
      if (last.includes('[')) {
        const [key, idx] = last.split('[')
        obj[key][parseInt(idx)] = value
      } else {
        obj[last] = value
      }
      return { ...m, config: c }
    })
  }

  const addCamera = () => {
    setCfgModal(m => {
      const cameras = [...(m.config.cameras || [])]
      const nextCh = cameras.length > 0 ? Math.max(...cameras.map(c => c.channel)) + 1 : 1
      cameras.push({ channel: nextCh, name: `Cámara ${nextCh}`, zone: 'general', analytics: ['lpr', 'vehicle', 'person'], fps_analytics: 2 })
      return { ...m, config: { ...m.config, cameras } }
    })
  }

  const removeCamera = (idx) => {
    setCfgModal(m => {
      const cameras = m.config.cameras.filter((_, i) => i !== idx)
      return { ...m, config: { ...m.config, cameras } }
    })
  }

  const toggleAnalytic = (camIdx, analytic) => {
    setCfgModal(m => {
      const cameras = JSON.parse(JSON.stringify(m.config.cameras))
      const cam = cameras[camIdx]
      const has = cam.analytics.includes(analytic)
      cam.analytics = has ? cam.analytics.filter(a => a !== analytic) : [...cam.analytics, analytic]
      return { ...m, config: { ...m.config, cameras } }
    })
  }

  const toggleAlertZone = (zone) => {
    setCfgModal(m => {
      const zones = m.config.analytics.person_alert_zones || []
      const has = zones.includes(zone)
      const newZones = has ? zones.filter(z => z !== zone) : [...zones, zone]
      return { ...m, config: { ...m.config, analytics: { ...m.config.analytics, person_alert_zones: newZones } } }
    })
  }

  const addNode = async () => {
    if (!addForm.site_id_ext.trim()) { setAddError('El ID del sitio es obligatorio'); return }
    setAddLoading(true); setAddError('')
    try {
      const r = await fetch('/api/edge/nodes', {
        method: 'POST',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id_ext: addForm.site_id_ext.trim(), site_name: addForm.site_name.trim() || null, api_key: Array.from(crypto.getRandomValues(new Uint8Array(32))).map(b => b.toString(16).padStart(2,'0')).join('') }),
      })
      if (!r.ok) {
        const e = await r.json().catch(() => ({}))
        setAddError(fmtDetail(e.detail, 'Error al crear el nodo'))
        return
      }
      const node = await r.json()
      setAddModal(false)
      setAddForm({ site_id_ext: '', site_name: '' })
      setKeyModal({ ...node, new_api_key: node.api_key })
      load()
    } finally { setAddLoading(false) }
  }

  const now = Date.now()

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{nodes.length} nodo{nodes.length !== 1 ? 's' : ''}</span>
        <span style={{ fontSize: 12, color: '#059669', marginLeft: 8 }}>● {nodes.filter(n => n.online).length} online</span>
        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>○ {nodes.filter(n => !n.online).length} offline</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={load} style={BTN({ background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-primary)' })}>↻ Actualizar</button>
          <button onClick={() => { setAddForm({ site_id_ext: '', site_name: '' }); setAddError(''); setAddModal(true) }}
            style={BTN({ background: 'var(--accent)', color: '#fff' })}>+ Agregar nodo</button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 12 }}>Cargando…</div>}

        {!loading && nodes.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 70px 70px 80px 80px 180px', padding: '8px 20px', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', position: 'sticky', top: 0 }}>
            <span>Sitio / ID externo</span><span>Versión</span><span>CPU</span><span>Mem</span><span>Cámaras</span><span>Último HB</span><span>Acciones</span>
          </div>
        )}

        {nodes.map(node => {
          const hb = node.last_heartbeat ? new Date(node.last_heartbeat) : null
          const agoMin = hb ? Math.floor((now - hb) / 60000) : null
          const agoStr = agoMin === null ? '—' : agoMin < 1 ? 'Ahora' : agoMin < 60 ? `${agoMin}m` : `${Math.floor(agoMin/60)}h${agoMin%60}m`

          return (
            <div key={node.id} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 70px 70px 80px 80px 180px', padding: '10px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 12, opacity: node.active ? 1 : 0.5 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <div>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: node.online ? '#10b981' : '#6b7280', display: 'inline-block', flexShrink: 0 }} />
                  {node.site_name || node.site_id_ext}
                  {node.has_remote_config && <span title="Config remota asignada" style={{ fontSize: 10, background: '#eff6ff', color: '#3b82f6', border: '1px solid #bfdbfe', borderRadius: 4, padding: '1px 5px', marginLeft: 4 }}>CFG✓</span>}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{node.site_id_ext}</div>
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{node.version || '—'}</span>
              <span style={{ fontSize: 11, color: node.cpu_pct > 80 ? '#ef4444' : 'var(--text-secondary)' }}>{node.cpu_pct != null ? `${node.cpu_pct}%` : '—'}</span>
              <span style={{ fontSize: 11, color: node.mem_pct > 80 ? '#ef4444' : 'var(--text-secondary)' }}>{node.mem_pct != null ? `${node.mem_pct}%` : '—'}</span>
              <span style={{ fontSize: 11 }}>{node.cameras_configured ?? '—'}</span>
              <span style={{ fontSize: 11, color: agoMin > 5 ? '#ef4444' : 'var(--text-secondary)' }}>{agoStr}</span>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <button onClick={() => openConfig(node)} disabled={cfgLoading} title="Configurar NVR, cámaras y analíticas" style={BTN({ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', padding: '4px 8px', fontSize: 11 })}>⚙ Configurar</button>
                <button onClick={() => regenerateKey(node)} title="Regenerar API key" style={BTN({ background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-primary)', padding: '4px 8px' })}>🔑</button>
                {node.active
                  ? <button onClick={() => deactivate(node)} style={BTN({ background: 'transparent', color: '#dc2626', border: '1px solid #fecaca', fontSize: 11, padding: '4px 8px' })}>✕</button>
                  : <button onClick={() => reactivate(node)} style={BTN({ background: '#f0fdf4', color: '#059669', border: '1px solid #bbf7d0', fontSize: 11, padding: '4px 8px' })}>↩</button>
                }
              </div>
            </div>
          )
        })}
        {!loading && nodes.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)', fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🖥</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Sin nodos edge registrados</div>
            <div style={{ fontSize: 12, marginBottom: 20 }}>Registra el primer Mac mini edge para empezar a recibir eventos y analíticas.</div>
            <button onClick={() => { setAddForm({ site_id_ext: '', site_name: '' }); setAddError(''); setAddModal(true) }}
              style={BTN({ background: 'var(--accent)', color: '#fff' })}>+ Agregar primer nodo</button>
          </div>
        )}
      </div>

      {/* Modal configuración remota */}
      {cfgModal && (
        <Modal title={`Configurar nodo: ${cfgModal.node.site_name || cfgModal.node.site_id_ext}`} onClose={() => setCfgModal(null)} width={740}>
          {/* Sub-tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 18, marginTop: -4 }}>
            {[['general','🏢 General'],['nvrs','📹 NVR'],['cameras','📷 Cámaras'],['analytics','🧠 Analíticas']].map(([tab, label]) => (
              <button key={tab} onClick={() => setCfgModal(m => ({ ...m, tab }))}
                style={{ padding: '7px 14px', border: 'none', borderBottom: cfgModal.tab === tab ? '2px solid var(--accent)' : '2px solid transparent', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: cfgModal.tab === tab ? 700 : 400, color: cfgModal.tab === tab ? 'var(--accent)' : 'var(--text-secondary)' }}>
                {label}
              </button>
            ))}
          </div>

          {/* Tab: General */}
          {cfgModal.tab === 'general' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Nombre del sitio">
                <input style={INPUT_STYLE} value={cfgModal.config.site_name} onChange={e => updateCfg('site_name', e.target.value)} placeholder="ej. Walmart Providencia Norte" />
              </Field>
              <Field label="Zona horaria">
                <select style={INPUT_STYLE} value={cfgModal.config.timezone} onChange={e => updateCfg('timezone', e.target.value)}>
                  {['America/Santiago','America/Lima','America/Bogota','America/Buenos_Aires','America/Sao_Paulo','America/Mexico_City','America/New_York','UTC'].map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </Field>
            </div>
          )}

          {/* Tab: NVR */}
          {cfgModal.tab === 'nvrs' && (() => {
            const nvr = cfgModal.config.nvrs[0] || {}
            const upd = (k, v) => updateCfg(`nvrs[0].${k}`, v)
            return (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>
                  Configura el NVR principal del sitio. El nodo edge se conectará a esta IP para obtener los streams RTSP.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Marca del NVR">
                    <select style={INPUT_STYLE} value={nvr.brand || 'dahua'} onChange={e => upd('brand', e.target.value)}>
                      <option value="dahua">Dahua</option>
                      <option value="hikvision">Hikvision</option>
                      <option value="axis">Axis</option>
                      <option value="hanwha">Hanwha</option>
                      <option value="generic">Genérico (RTSP)</option>
                    </select>
                  </Field>
                  <Field label="Nombre">
                    <input style={INPUT_STYLE} value={nvr.name || ''} onChange={e => upd('name', e.target.value)} placeholder="NVR Principal" />
                  </Field>
                  <Field label="IP / Host *">
                    <input style={{ ...INPUT_STYLE, fontFamily: 'monospace' }} value={nvr.host || ''} onChange={e => upd('host', e.target.value)} placeholder="192.168.1.108" />
                  </Field>
                  <Field label="Puerto HTTP">
                    <input style={INPUT_STYLE} type="number" value={nvr.port || 80} onChange={e => upd('port', parseInt(e.target.value))} />
                  </Field>
                  <Field label="Puerto RTSP">
                    <input style={INPUT_STYLE} type="number" value={nvr.rtsp_port || 554} onChange={e => upd('rtsp_port', parseInt(e.target.value))} />
                  </Field>
                  <Field label="Canales">
                    <input style={INPUT_STYLE} type="number" min={1} max={64} value={nvr.channels || 8} onChange={e => upd('channels', parseInt(e.target.value))} />
                  </Field>
                  <Field label="Usuario">
                    <input style={INPUT_STYLE} value={nvr.username || 'admin'} onChange={e => upd('username', e.target.value)} />
                  </Field>
                  <Field label="Contraseña">
                    <input style={INPUT_STYLE} type="password" value={nvr.password || ''} onChange={e => upd('password', e.target.value)} placeholder="Se guardará en Keychain del Mac mini" />
                  </Field>
                </div>
                <div style={{ marginTop: 10, fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg-muted)', borderRadius: 6, padding: '8px 12px' }}>
                  💡 La contraseña se transmite cifrada y el nodo la guarda en el macOS Keychain — nunca en texto plano.
                </div>
              </div>
            )
          })()}

          {/* Tab: Cámaras */}
          {cfgModal.tab === 'cameras' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{cfgModal.config.cameras.length} cámaras configuradas</span>
                <button onClick={addCamera} style={{ ...BTN({ background: 'var(--accent)', color: '#fff', fontSize: 11 }), marginLeft: 'auto' }}>+ Agregar cámara</button>
              </div>
              {cfgModal.config.cameras.length === 0 && (
                <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 12 }}>
                  Sin cámaras configuradas — agrega una cámara para empezar.
                </div>
              )}
              {cfgModal.config.cameras.map((cam, idx) => (
                <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: 'var(--bg-muted)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr 60px', gap: 10, alignItems: 'end', marginBottom: 10 }}>
                    <Field label="Canal">
                      <input style={INPUT_STYLE} type="number" min={1} value={cam.channel} onChange={e => updateCfg(`cameras[${idx}].channel`, parseInt(e.target.value))} />
                    </Field>
                    <Field label="Nombre">
                      <input style={INPUT_STYLE} value={cam.name} onChange={e => updateCfg(`cameras[${idx}].name`, e.target.value)} placeholder="ej. Entrada Vehicular" />
                    </Field>
                    <Field label="Zona">
                      <select style={INPUT_STYLE} value={cam.zone} onChange={e => updateCfg(`cameras[${idx}].zone`, e.target.value)}>
                        {COMMON_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
                      </select>
                    </Field>
                    <Field label="FPS">
                      <input style={INPUT_STYLE} type="number" min={1} max={15} value={cam.fps_analytics || 2} onChange={e => updateCfg(`cameras[${idx}].fps_analytics`, parseInt(e.target.value))} />
                    </Field>
                  </div>
                  <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Analíticas:</span>
                    {ANALYTICS_OPTS.map(a => (
                      <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer' }}>
                        <input type="checkbox" checked={cam.analytics?.includes(a)} onChange={() => toggleAnalytic(idx, a)} style={{ cursor: 'pointer' }} />
                        {a === 'lpr' ? '🚗 LPR' : a === 'vehicle' ? '🚙 Vehículos' : '👤 Personas'}
                      </label>
                    ))}
                    <button onClick={() => removeCamera(idx)} style={{ ...BTN({ background: 'transparent', color: '#dc2626', fontSize: 11 }), marginLeft: 'auto' }}>✕ Eliminar</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tab: Analíticas */}
          {cfgModal.tab === 'analytics' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <Field label="Confianza detección (0-1)">
                  <input style={INPUT_STYLE} type="number" step={0.05} min={0.1} max={1} value={cfgModal.config.analytics.confidence}
                    onChange={e => updateCfg('analytics.confidence', parseFloat(e.target.value))} />
                </Field>
                <Field label="Confianza LPR (0-1)">
                  <input style={INPUT_STYLE} type="number" step={0.05} min={0.1} max={1} value={cfgModal.config.analytics.lpr_confidence}
                    onChange={e => updateCfg('analytics.lpr_confidence', parseFloat(e.target.value))} />
                </Field>
                <Field label="Área mínima placa (px²)">
                  <input style={INPUT_STYLE} type="number" min={100} value={cfgModal.config.analytics.lpr_min_area}
                    onChange={e => updateCfg('analytics.lpr_min_area', parseInt(e.target.value))} />
                </Field>
                <Field label="Tiempo dwell vehículo (seg)">
                  <input style={INPUT_STYLE} type="number" min={10} value={cfgModal.config.analytics.vehicle_dwell_seconds}
                    onChange={e => updateCfg('analytics.vehicle_dwell_seconds', parseInt(e.target.value))} />
                </Field>
              </div>
              <Field label="Zonas con alerta de intrusión de personas">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {COMMON_ZONES.map(zone => {
                    const active = cfgModal.config.analytics.person_alert_zones?.includes(zone)
                    return (
                      <button key={zone} onClick={() => toggleAlertZone(zone)}
                        style={BTN({ background: active ? '#dc2626' : 'var(--bg-muted)', color: active ? '#fff' : 'var(--text-primary)', border: `1px solid ${active ? '#dc2626' : 'var(--border)'}`, fontSize: 11 })}>
                        {zone}
                      </button>
                    )
                  })}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>{_svEN ? 'Zones marked in red will trigger alerts when persons are detected.' : 'Zonas marcadas en rojo generarán alertas cuando se detecten personas.'}</div>
              </Field>
              <Field label="Ruta del modelo (en el Mac mini)">
                <input style={{ ...INPUT_STYLE, fontFamily: 'monospace' }} value={cfgModal.config.analytics.model_path}
                  onChange={e => updateCfg('analytics.model_path', e.target.value)} />
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>Mac M4: .mlpackage (CoreML) | Intel: .onnx</div>
              </Field>
            </div>
          )}

          {cfgModal.error && (
            <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', marginTop: 14 }}>{cfgModal.error}</div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button onClick={() => setCfgModal(null)} style={BTN({ background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-primary)' })}>{_svEN ? 'Cancel' : 'Cancelar'}</button>
            <button onClick={saveConfig} disabled={cfgModal.saving}
              style={BTN({ background: 'var(--accent)', color: '#fff', opacity: cfgModal.saving ? 0.6 : 1 })}>
              {cfgModal.saving ? 'Guardando…' : '💾 Guardar y enviar al nodo'}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal agregar nodo */}
      {addModal && (
        <Modal title="Registrar nodo edge" onClose={() => setAddModal(false)} width={460}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.5 }}>
            Registra el Mac mini que actuará como nodo edge en un local. Se generará una API Key que deberás copiar al <code>site.json</code> del equipo.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>ID del sitio <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                value={addForm.site_id_ext}
                onChange={e => setAddForm(f => ({ ...f, site_id_ext: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addNode()}
                placeholder="ej. copec-providencia-001"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-muted)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }}
                autoFocus
              />
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>Debe coincidir exactamente con el <code>site_id</code> en el site.json del edge.</div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Nombre del sitio</label>
              <input
                value={addForm.site_name}
                onChange={e => setAddForm(f => ({ ...f, site_name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addNode()}
                placeholder="ej. Copec Providencia Norte"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-muted)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
          </div>
          {addError && <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', marginBottom: 14 }}>{addError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setAddModal(false)} style={BTN({ background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-primary)' })}>{_svEN ? 'Cancel' : 'Cancelar'}</button>
            <button onClick={addNode} disabled={addLoading} style={BTN({ background: 'var(--accent)', color: '#fff', opacity: addLoading ? 0.6 : 1 })}>
              {addLoading ? 'Registrando…' : 'Registrar y obtener API Key'}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal nueva API key */}
      {keyModal && (
        <Modal title="API Key del nodo edge" onClose={() => setKeyModal(null)} width={480}>
          <div style={{ marginBottom: 12, fontSize: 13 }}>
            Nodo: <strong>{keyModal.site_name || keyModal.site_id_ext}</strong>
          </div>
          <div style={{ padding: '12px 16px', background: 'var(--bg-muted)', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', border: '1px solid var(--border)', marginBottom: 12 }}>
            {keyModal.new_api_key}
          </div>
          <div style={{ fontSize: 11, color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
            ⚠️ Copia esta clave ahora. No se mostrará de nuevo.
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>
            Pégala en el <code>bootstrap.json</code> del Mac mini bajo <code>"cloud": {'{'}"api_key": "..."{'}'}</code>, o ejecuta <code>scripts/provision-secrets.sh</code> y pégala cuando lo pida. Luego usa <strong>⚙ Configurar</strong> para asignar NVRs y cámaras remotamente.
          </div>
          <button onClick={() => { navigator.clipboard?.writeText(keyModal.new_api_key); setKeyModal(null) }}
            style={BTN({ background: 'var(--accent)', color: '#fff', width: '100%', textAlign: 'center' })}>
            📋 Copiar y cerrar
          </button>
        </Modal>
      )}
    </div>
  )
}


// ─── PreferencesTab ───────────────────────────────────────────────────────────

function PreferencesTab() {
  const { skin, skins, setSkin } = useSkinStore()

  return (
    <div style={{ padding: 24, maxWidth: 620 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Apariencia</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Elige el tema visual del sistema. El cambio se aplica inmediatamente y se recuerda entre sesiones.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {skins.map(s => (
          <button
            key={s.id}
            onClick={() => setSkin(s.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: '14px 16px',
              borderRadius: 'var(--radius-md)',
              border: `2px solid ${skin === s.id ? 'var(--accent)' : 'var(--border)'}`,
              background: skin === s.id ? 'var(--accent-light)' : 'var(--bg-surface)',
              cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
            }}
          >
            {/* Paleta de colores */}
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {s.preview.map((color, i) => (
                <div key={i} style={{
                  width: i === 0 ? 28 : 16,
                  height: 28,
                  borderRadius: 6,
                  background: color,
                  border: '1px solid rgba(0,0,0,0.15)',
                }} />
              ))}
            </div>

            {/* Info */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>
                {s.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.desc}</div>
            </div>

            {/* Check activo */}
            {skin === s.id && (
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: 'var(--accent)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, flexShrink: 0,
              }}>✓</div>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Tabs config + export ─────────────────────────────────────────────────────

const TABS = [
  { id: 'sites',   Icon: MapPin,    label: 'Sitios',           desc: 'Gestión de ubicaciones' },
  { id: 'users',   Icon: Users,     label: 'Usuarios',         desc: 'Cuentas y permisos' },
  { id: 'devices', Icon: Cpu,       label: 'Dispositivos',     desc: 'Cámaras y sensores por sitio' },
  { id: 'fmtzones',Icon: Layers,    label: 'Formatos / Zonas', desc: 'Estructura del negocio' },
  { id: 'edge',    Icon: Server,    label: 'Edge Nodes',       desc: 'Nodos de analítica' },
  { id: 'nvrs',    Icon: HardDrive, label: 'NVRs',             desc: 'Grabadoras de video' },
  { id: 'notifs',  Icon: Bell,      label: 'Notificaciones',   desc: 'Árbol de escalación' },
  { id: 'prefs',   Icon: Palette,   label: 'Preferencias',     desc: 'Apariencia y tema', always: true },
]

export default function SettingsView() {
  const token = useAuthStore(s => s.token)
  const user  = useAuthStore(s => s.user)
  const [tab, setTab] = useState('sites')

  // Ocultar tabs de admin si el usuario no tiene permisos
  const visibleTabs = TABS.filter(t => {
    if (t.always) return true
    if (['users', 'fmtzones', 'edge'].includes(t.id)) return user?.can_manage
    return true
  })

  return (
    <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      <div style={{ width: 200, background: 'var(--bg-surface)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', padding: '16px 8px', gap: 2, flexShrink: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', padding: '0 8px 8px', letterSpacing: '0.05em' }}>{_svEN ? 'Configuration' : 'Configuración'}</div>
        {visibleTabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer', background: tab === t.id ? 'var(--accent)' : 'transparent', color: tab === t.id ? '#fff' : 'var(--text-secondary)', fontSize: 13, fontWeight: tab === t.id ? 600 : 400, transition: 'all 0.1s', display: 'flex', alignItems: 'center', gap: 8 }}>
            <t.Icon size={14} strokeWidth={1.75} style={{ flexShrink: 0 }} />
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {tab === 'sites'    && <SitesTab          token={token} />}
        {tab === 'users'    && <UsersTab           token={token} />}
        {tab === 'devices'  && <DevicesTab         token={token} />}
        {tab === 'fmtzones' && <FormatsZonesTab    token={token} />}
        {tab === 'edge'     && <EdgeNodesTab       token={token} />}
        {tab === 'nvrs'     && <NVRsTab            token={token} />}
        {tab === 'notifs'   && <NotificationsTab   token={token} />}
        {tab === 'prefs'    && <PreferencesTab />}
      </div>
    </div>
  )
}